import { Card } from 'react-bootstrap';
import { Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useState, useRef, useCallback } from 'react';

// `to` — detail page link; callers in a scoped list (e.g. My Benchmarks) pass their own
const Benchmark = ({ benchmark, to = `/all_benchmarks/benchmark/${benchmark._id}` }) => {
  // 2. Data logic (runs every time the component renders)
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const writeSpeed = benchmark.operations.find(op => op.modeDisplay?.toUpperCase().includes("WRITE"))?.bandwidth;
  const readSpeed = benchmark.operations.find(op => op.modeDisplay?.toUpperCase().includes("READ"))?.bandwidth;

  // Tooltip state
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const tooltipTimer = useRef(null);

  const handleMouseEnter = useCallback(() => {
    tooltipTimer.current = setTimeout(() => setTooltipVisible(true), 1000);
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(tooltipTimer.current);
    setTooltipVisible(false);
  }, []);

  const handleMouseMove = useCallback((e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  }, []);

  // 3. The Layout
  return (
    <>
    <Card
      className='mb-3 shadow-sm'
      onClick={() => {
        clearTimeout(tooltipTimer.current);
        setTooltipVisible(false);
        navigate(to);
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      style={{ cursor: 'pointer' }}
    >
      <Card.Body>
        <Row className='align-items-center'>
          {/* Drive Info */}
          <Col md={5} className='wrap_early-1'>
            {/* #17 current format vs pre refactor format */}
            <h5 className='mb-0 text-primary'>
              {benchmark?.driveInfo?.driveModel ?? benchmark?.driveModel}
            </h5>
            <div className='small text-muted'>
              {benchmark?.systemInfo?.os ?? benchmark?.os} - {benchmark?.systemInfo?.processorName ?? benchmark?.processorName}
            </div>
            <div className='small text-muted'>
              {benchmark.config?.profile?.name}
              {userInfo?.isAdmin && benchmark.config?.appVersion && (
                <span className='ms-2'>v{benchmark.config.appVersion}</span>
              )}
              {benchmark.profileModified && (
                <span
                  className='benchmark__modified-badge ms-2'
                  title='Run with modified profile settings'
                >
                  Modified
                </span>
              )}
            </div>
          </Col>

          {/* Integration Code: Metrics Section */}
          <Col md={4} className='benchmark__stats'>
            <Row className='benchmark__stats-row'>
              {/* Read Speed Section */}
              <Col>
                <div className='small text-muted'>READ</div>
                <div className='fw-bold'>
                  {readSpeed ? <>{Number(readSpeed).toFixed(2)} <span className='fw-normal small'>MB/s</span></> : "--"}
                </div>
              </Col>

              {/* Write Speed Section */}
              <Col>
                <div className='small text-muted'>WRITE</div>
                <div className='fw-bold'>
                  {writeSpeed ? <>{Number(writeSpeed).toFixed(2)} <span className='fw-normal small'>MB/s</span></> : "--"}
                </div>
              </Col>
            </Row>
          </Col>

          {/* Time */}
          <Col md={3} className='text-end benchmark__timestamp'>
            <div className='small text-muted'>
              {/* Option 1: Formatting the date object on the fly.
                  'unknown' offsets store the tester's wall clock as if UTC, so
                  render it unconverted rather than shifting to the viewer's zone */}
              {new Date(benchmark.startTimeString).toLocaleString([], {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                ...(benchmark.utcOffsetSource === 'unknown' && { timeZone: 'UTC' }),
              })}
            </div>
            {benchmark.username && benchmark.username !== 'anonymous' && (
              <div className='small text-muted fst-italic'>
                by {benchmark.username}
              </div>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>

    {/* Mouse-following tooltip */}
    {tooltipVisible && (
      <div
        className='benchmark-tooltip'
        style={{ left: mousePos.x + 14, top: mousePos.y + 14 }}
      >
        View Details
      </div>
    )}
    </>
  );
};

export default Benchmark;